import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Modal, Skeleton, useToast } from '../../../design-system';
import { money, fmtDate } from '../../pages/OrgAdminFinance';
import '../../styles/Finance.css';

// Clients telling the firm they have paid, waiting to be checked against the
// bank.
//
// The queue exists because a client's word is not a receipt. Nothing in here
// has touched the ledger yet — approving is the moment money becomes real, and
// the confirm dialog spells out everything that will happen, because it is
// several irreversible things at once (a payment booked, a statutory invoice
// number consumed, a receipt emailed to the client).

const MODE_LABEL = {
  bank_transfer: 'Bank transfer', upi: 'UPI', cheque: 'Cheque',
  cash: 'Cash', card: 'Card', other: 'Other',
};

const FILTERS = [
  { key: 'pending', label: 'To review' },
  { key: 'approved', label: 'Confirmed' },
  { key: 'rejected', label: 'Rejected' },
];

function PaymentReviewQueue({ onChanged }) {
  const toast = useToast();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [amounts, setAmounts] = useState({ amount: '', tdsAmount: '' });

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get('/api/finance/payment-advices', { params: { status } })
      .then((r) => setRows(r.data.rows))
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load payment declarations'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  useEffect(load, [load]);

  const openConfirm = (a) => {
    setConfirming(a);
    // Default to what the client claimed. The reviewer changes it only when the
    // bank says otherwise — which is the whole reason it is editable.
    setAmounts({ amount: String(a.amount ?? ''), tdsAmount: String(a.tdsAmount ?? '') });
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/api/finance/payment-advices/${confirming.id}/approve`, {
        amount: Number(amounts.amount) || 0,
        tdsAmount: Number(amounts.tdsAmount) || 0,
      });
      const bits = [`Receipt ${r.data.payment?.receiptNumber}`];
      if (r.data.issuedNow) bits.push(`invoice ${r.data.invoice?.invoiceNumber} issued`);
      if (r.data.delivery?.status === 'sent') bits.push(`emailed to ${r.data.delivery.to.join(', ')}`);
      else if (r.data.delivery?.status === 'skipped') bits.push('not emailed — no address / SMTP off');
      else if (r.data.delivery?.status === 'failed') bits.push('email failed');
      toast.success(bits.join(' · '));
      setConfirming(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not confirm that payment');
    } finally {
      setBusy(false);
    }
  };

  const reject = async (a) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      'Why can this payment not be confirmed?\nThe client sees this, so make it actionable.'
    );
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/finance/payment-advices/${a.id}/reject`, { reason });
      toast.success('Rejected — the client has been told why');
      load();
      onChanged?.();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not reject that');
    } finally {
      setBusy(false);
    }
  };

  const openProof = async (a) => {
    try {
      const r = await apiClient.get(`/api/finance/payment-advices/${a.id}/proof`);
      window.open(r.data.url, '_blank', 'noopener');
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not open that file');
    }
  };

  const claimed = confirming ? (confirming.amount || 0) + (confirming.tdsAmount || 0) : 0;
  const confirmingTotal = (Number(amounts.amount) || 0) + (Number(amounts.tdsAmount) || 0);
  const differs = confirming && Math.abs(confirmingTotal - claimed) > 0.5;

  return (
    <div className="fin-stack">
      <Card>
        <h3 className="fin-h">Payments clients have reported</h3>
        <p className="fin-sub">
          Nothing here has been recorded yet. Match each one against your bank statement, then
          confirm — that is what books the money, issues the tax invoice for a proforma, and sends
          the client their receipt.
        </p>

        <div className="fin-filter-row">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`fin-chip${status === f.key ? ' is-active' : ''}`}
              onClick={() => setStatus(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading || !rows ? <Skeleton.Text lines={5} /> : rows.length === 0 ? (
          <p className="fin-sub" style={{ marginBottom: 0 }}>
            {status === 'pending' ? 'Nothing waiting — you are up to date.' : 'Nothing here.'}
          </p>
        ) : (
          <div className="fin-scroll">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Against</th>
                  <th>Reported</th>
                  <th className="fin-num">Cash</th>
                  <th className="fin-num">TDS</th>
                  <th className="fin-num">Invoice due</th>
                  <th>Reference</th>
                  {status === 'pending' && <th className="fin-sticky-col">Action</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const total = a.claimedTotal;
                  const mismatch = a.invoiceAmountDue != null && Math.abs(total - a.invoiceAmountDue) > 0.5;
                  return (
                    <tr key={a.id}>
                      <td>{a.clientName}</td>
                      <td className="fin-nowrap">
                        {a.invoiceNumber || a.proformaNumber || '—'}
                        {!a.invoiceNumber && a.proformaNumber && (
                          <span className="fin-pill fin-s-draft" style={{ marginLeft: 6 }}>Proforma</span>
                        )}
                      </td>
                      <td className="fin-nowrap">
                        {fmtDate(a.paidOn)}
                        <span className="fin-muted"> · {MODE_LABEL[a.mode] || a.mode}</span>
                      </td>
                      <td className="fin-num">{money(a.amount)}</td>
                      <td className="fin-num fin-muted">{a.tdsAmount ? money(a.tdsAmount) : '—'}</td>
                      <td className={`fin-num${mismatch ? ' fin-bad' : ''}`}>
                        {a.invoiceAmountDue != null ? money(a.invoiceAmountDue) : '—'}
                      </td>
                      <td className="fin-muted">
                        {a.reference || '—'}
                        {a.documentId && (
                          <>
                            {' '}
                            <button type="button" className="fin-linkbtn" onClick={() => openProof(a)}>proof</button>
                          </>
                        )}
                        {a.status === 'rejected' && a.rejectionReason && (
                          <div className="fin-bad">{a.rejectionReason}</div>
                        )}
                      </td>
                      {status === 'pending' && (
                        <td className="fin-sticky-col fin-nowrap">
                          <Button size="sm" variant="primary" onClick={() => openConfirm(a)} disabled={busy}>
                            Confirm
                          </Button>
                          {' '}
                          <Button size="sm" variant="ghost" onClick={() => reject(a)} disabled={busy}>
                            Reject
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {confirming && (
        <Modal
          isOpen
          onClose={() => setConfirming(null)}
          title={`Confirm payment from ${confirming.clientName}`}
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirming(null)}>Cancel</Button>
              <Button variant="primary" onClick={approve} loading={busy}>
                Confirm and record
              </Button>
            </>
          }
        >
          <div className="fin-form">
            <p className="fin-sub" style={{ marginTop: 0 }}>
              {confirming.clientName} reported <b>{money(claimed)}</b> on {fmtDate(confirming.paidOn)}
              {confirming.reference ? <> with reference <b>{confirming.reference}</b></> : null}.
              Record what actually reached the bank.
            </p>

            <div className="fin-form-row">
              <Input
                label="Cash received" type="number" min="0" step="0.01"
                value={amounts.amount}
                onChange={(e) => setAmounts((a) => ({ ...a, amount: e.target.value }))}
              />
              <Input
                label="TDS deducted" type="number" min="0" step="0.01"
                value={amounts.tdsAmount}
                onChange={(e) => setAmounts((a) => ({ ...a, tdsAmount: e.target.value }))}
              />
            </div>

            {differs && (
              <p className="fin-note-warn">
                You're recording {money(confirmingTotal)}, not the {money(claimed)} the client reported.
                Their declaration keeps the original figure for comparison.
              </p>
            )}

            {/* Confirming is several irreversible acts at once. Saying which
                beats a generic "are you sure". */}
            <div className="fin-willhappen">
              <span className="fin-form-label">This will</span>
              <ul>
                <li>record {money(confirmingTotal)} against {confirming.invoiceNumber || confirming.proformaNumber}</li>
                {!confirming.invoiceNumber && (
                  <li><b>issue the tax invoice</b> — receiving payment is the time of supply, so a
                    statutory number is assigned and dated {fmtDate(confirming.paidOn)}</li>
                )}
                <li>email the client a receipt</li>
              </ul>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default PaymentReviewQueue;
